import React, { Dispatch, SetStateAction, useState, useEffect, useCallback, useRef } from "react";
import { Modal, Box, Button, DialogActions, Autocomplete, TextField, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Text } from "@mantine/core";
import dayjs, { Dayjs } from "dayjs";
import FormHeader from "../../../components/dms_common/FormHeader";
import FormTextField from "../../../components/dms_common/FormTextField";
import FormDateField from "../../../components/dms_common/FormDateField";
import "../../../components/dms_common/FormTextField.css";
import { BatchAndExpiryManagementRowsData, popupType } from "../../../types/types";
import { ChannelkartNetworkPut } from "../../../services/networkService";
import { GenericPopUp } from "../../../components/popup/genericPopUp";
import { getCasePieceSumProps } from "../getQuantityProps";
import { Loader } from "../../../components/loader/Loader";
import { transformToBatchwiseRowsData } from "./BatchAndExpiryManagementUtils";
import { getDataFromLocalDb } from "../../../services/dbService";
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import "./CreateBatchIdForm.css";
import { customStyle, GenericModal, getGenericModalProps, initialGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import { logAnalyticsEvent, logLoadTrace } from "../../../firebase";
import { EnvironmentRegistry } from "../../../services/registry/environmentRegistry";
import useShortcutKeys from "../../../components/dms_common/ShortcutKeys";

const customMuiInputStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover fieldset": {
      borderColor: "#B9B9B9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1C7EA0",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#B5B5B5",
    fontSize: "13px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1C7EA0",
  },
};

const modalStyle = {
  position: "absolute" as "absolute",
  borderRadius: "8px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "32vw",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

interface FormData {
  skuName: string;
  skuCode: string;
  invoiceNumber: string;
  batchId: string;
  quantityCs: string;
  quantityPc: string;
  quantityOther: string;
  expiryDate: Dayjs | null;
}

const initialFormData = {
  skuName: "",
  skuCode: "",
  invoiceNumber: "",
  batchId: "",
  quantityCs: "",
  quantityPc: "",
  quantityOther: "",
  expiryDate: null,
};

interface CreateBatchIdFormProps {
  setOpenCreateBatchIdForm: Dispatch<SetStateAction<boolean>>;
  skuProductDescriptionMap: Record<string, string>;
  skuUomMap: Record<string, string>;
}

const CreateBatchIdForm: React.FC<CreateBatchIdFormProps> = ({
  setOpenCreateBatchIdForm,
  skuProductDescriptionMap,
  skuUomMap,
}) => {
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expiryError, setExpiryError] = useState("");
  const [batchIdError, setBatchIdError] = useState("");
  const [unallocatedQtyDisclamer, setUnallocatedDisclamer] = useState("");
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const existingBatchIdsRef = useRef<BatchAndExpiryManagementRowsData[]>([]);
  const unallocatedQty = useRef({
    cs: 0,
    pc: 0,
    other: 0,
  });
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [applyLoading, setApplyLoading] = useState<boolean>(false)

  const skuNameToCodeMap = Object.fromEntries(
    Object.entries(skuProductDescriptionMap).map(([skuCode, skuName]) => [skuName, skuCode])
  );

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel } = ConfigDictionary;
  const { positiveCta } = useShortcutKeys();


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "quantityCs" || name === "quantityPc" || name === "quantityOther") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData({
        ...formData,
        [name]: numericValue,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  function findBatchRecords(batchId: string): {
    count: number;
    firstRecord: BatchAndExpiryManagementRowsData | null;
  } {
    const matchingRecords = existingBatchIdsRef.current.filter(
      (record) => record.batchId.toLowerCase() === batchId.toLowerCase()
    );
    return {
      count: matchingRecords.length,
      firstRecord: matchingRecords.length > 0 ? matchingRecords[0] : null,
    };
  }

  function findUnallocatedQty(skuCode: string): {
    totalCaseQty: number;
    totalPieceQty: number;
    totalOtherQty: number;
  } {
    const matchingRecords = existingBatchIdsRef.current.filter(
      (record) =>
        record.skuCode.toLowerCase() === skuCode.toLowerCase() &&
        record.batchId.toLowerCase().includes("unassigned")
    );

    let totalCaseQty = 0;
    let totalPieceQty = 0;
    let totalOtherQty = 0;

    matchingRecords.forEach((record) => {
      totalCaseQty += record.caseAvailableQty;
      totalPieceQty += record.pieceAvailableQty;
      totalOtherQty += record.otherAvailableQty;
    });

    return {
      totalCaseQty: totalCaseQty,
      totalPieceQty: totalPieceQty,
      totalOtherQty: totalOtherQty,
    };
  }

  const setQtyDisclaimer = () => {
    if (formData.skuCode) {
      const { totalCaseQty, totalPieceQty, totalOtherQty } = findUnallocatedQty(formData.skuCode);
      if (totalCaseQty > 0 || totalPieceQty > 0 || totalOtherQty > 0) {
        const qtyStr = otherUnitLabel
          ? `${totalCaseQty} ${caseUnitLabel} ${totalOtherQty} ${otherUnitLabel} ${totalPieceQty} ${packUnitLabel}`
          : `${totalCaseQty} ${caseUnitLabel} ${totalPieceQty} ${packUnitLabel}`;
        setUnallocatedDisclamer("Unallocated Qty: " + qtyStr);
        unallocatedQty.current = { cs: totalCaseQty, pc: totalPieceQty, other: totalOtherQty };
      } else {
        setUnallocatedDisclamer(`No unallocated quantity exists for this SKU`);
        unallocatedQty.current = { cs: 0, pc: 0, other: 0 };
      }
    } else {
      setUnallocatedDisclamer("");
    }
  };

  const validateBatchId = () => {
    const { count, firstRecord } = findBatchRecords(formData.batchId);
    if (count > 0 && firstRecord) {
      if (firstRecord.skuCode.toLowerCase() === formData.skuCode.toLowerCase()) {
        setFormData({
          ...formData,
          expiryDate: dayjs(firstRecord?.expiryDate),
        });
        const { pcQty, csQty, otherQty } = getCasePieceSumProps(firstRecord);
        const qtyStr = otherUnitLabel
          ? `${csQty} ${caseUnitLabel}, ${otherQty} ${otherUnitLabel} and ${pcQty} ${packUnitLabel}`
          : `${csQty} ${caseUnitLabel} and ${pcQty} ${packUnitLabel}`;
        setBatchIdError(qtyStr + " for the selected Batch ID exist in your stock.");
      } else {
        setBatchIdError(`Batch ID already exists for another SKU. Please enter a unique Batch ID`);
      }
    } else {
      setBatchIdError("");
    }
  };

  useEffect(() => {
    validateBatchId();
    setQtyDisclaimer();
  }, [formData.skuCode, formData.skuName, formData.batchId]);

  const handleDateChange = (date: Dayjs | null, keyboardInputValue?: string) => {
    if (date && dayjs(date).isValid()) {
      if (dayjs(date).isBefore(dayjs().add(0, "day"))) {
        setExpiryError("Dates older than tomorrow can’t be entered.");
      } else {
        setExpiryError("");
      }
        setFormData({
          ...formData,
          expiryDate: date,
        });
    } else if (keyboardInputValue) {
      const parsedDate = dayjs(keyboardInputValue, "DD/MM/YYYY", true);
      if (parsedDate.isValid()) {
        if (parsedDate.isBefore(dayjs().add(0, "day"))) {
          setExpiryError("Dates older than tomorrow can’t be entered.");
        } else {
          setExpiryError("");
        }
          setFormData({
            ...formData,
            expiryDate: parsedDate,
          });
      } else {
        setExpiryError("Invalid date format. Please use DD/MM/YYYY.");
      }
    } else {
      setExpiryError("");
      setFormData({
        ...formData,
        expiryDate: null,
      });
    }
  };


  const openErrorPopup = () => {
    setGenericModalProps(
      getGenericModalProps("error", `Something Went Wrong!`)
    );
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(applyLoading) {
      return;
    }
    const payload = {
      skuCode: formData.skuCode,
      batchId: formData.batchId,
      pieceQty: formData.quantityPc,
      caseQty: formData.quantityCs,
      otherQty: formData.quantityOther,
      invoiceNumber: formData.invoiceNumber,
      expiryDate: formData.expiryDate ? formData.expiryDate.toISOString() : null,
      supplier: JSON.parse(localStorage.authContext).user.loginId,
      warehouseId: "",
      outletCode: "",
    };
    if (!expiryError) {
      setApplyLoading(true)
      try {
        const response = await ChannelkartNetworkPut(
          `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/assignBatch`,
          payload
        );
        if (response.status === 200) {
          logAnalyticsEvent( "batch_creation_success", {
            "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            "batch_id":formData.batchId,
            "sku_code":formData.skuCode,
            "expiry_date":formData.expiryDate
          });
          setFormData(initialFormData);
          setIsModalOpen(true);
        } else {
          openErrorPopup()
          logAnalyticsEvent( "batch_creation_failed", {
            "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            "error_reason": "API failure"
          });
        }
      } catch (error) {
        openErrorPopup()
        logAnalyticsEvent( "batch_creation_failed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "error_reason": "process error"
        });
      } finally {
        setApplyLoading(false)
      }
    } else {
      openErrorPopup()
      logAnalyticsEvent( "batch_creation_failed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "error_reason": "expiry error"
      });
    }
  };

  const resetErrors = () => {
    setExpiryError("");
    setBatchIdError("");
  };

  const handleSkuCodeChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    setFormData({
      ...initialFormData,
      skuCode: value || "",
      skuName: value ? skuProductDescriptionMap[value] : "",
    });
    resetErrors();
  };

  const handleSkuNameChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    setFormData({
      ...initialFormData,
      skuName: value || "",
      skuCode: value ? skuNameToCodeMap[value] : "",
    });
    resetErrors();
  };

  const closeBatchIdFormSubmitModal = () => {
    setOpenCreateBatchIdForm(false);
    setIsModalOpen(false);
  };

  const setInitialData = async () => {
    const batchData = await getDataFromLocalDb("BatchWiseStockData");
    existingBatchIdsRef.current = transformToBatchwiseRowsData(batchData.data);
  };

  const init = async () => {
    setIsLoading(true);
    await setInitialData();
    setIsLoading(false);
  };

  useEffect(() => {
    logLoadTrace(init,"create_stock_batch_page_load_time");
  }, []);

  const BatchIdFormSubmitModal = () => {
    return (
      <Modal open={isModalOpen} onClose={closeBatchIdFormSubmitModal}>
        <Box sx={modalStyle}>
          <CheckCircleIcon style={{ color: "#00C6B1", fontSize: "40px" }} />
          <Text
            sx={() => ({
              marginBottom: "3%",
              marginTop: "5%",
              fontSize: "15px",
              color: "#707070",
            })}
          >
            A new batch ID has been created
          </Text>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "var(--dashboard-theme-color)",
                ":hover": {
                  background: "var(--dashboard-theme-color)",
                },
                fontSize: "14px",
                padding: "0.5rem 1.5rem",
                boxShadow: "none",
              }}
              onClick={closeBatchIdFormSubmitModal}
            >
              Continue
            </Button>
          </DialogActions>
        </Box>
      </Modal>
    );
  };

  // Function to validate if all mandatory fields are filled and valid
  useEffect(() => {
    const validateForm = () => {
      const isFormValid =
        formData.skuCode &&
        formData.skuName &&
        formData.batchId &&
        !expiryError &&
        (formData.quantityCs || formData.quantityPc || formData.quantityOther) &&
        formData.expiryDate &&
        batchIdError !==
          "Batch ID already exists for another SKU. Please enter a unique Batch ID" &&
        (unallocatedQty.current.cs || unallocatedQty.current.pc || unallocatedQty.current.other) &&
        unallocatedQty.current.cs >= (formData.quantityCs ? parseInt(formData.quantityCs) : 0) &&
        unallocatedQty.current.pc >= (formData.quantityPc ? parseInt(formData.quantityPc) : 0) &&
        unallocatedQty.current.other >=
          (formData.quantityOther ? parseInt(formData.quantityOther) : 0);
      setIsApplyDisabled(!isFormValid);
    };
    validateForm();
  }, [formData, batchIdError, expiryError]);

  useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      event.preventDefault();
      if (!isApplyDisabled && !applyLoading && !isLoading) {
        const formEvent = new Event('submit', { bubbles: true, cancelable: true }) as unknown as React.FormEvent<HTMLFormElement>;
        handleSubmit(formEvent);
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [isApplyDisabled, applyLoading, isLoading, handleSubmit]);

  return (
    <>
      {isLoading ? (
        <Loader sx={{ position: "absolute", top: "45%" }} />
      ) : (
        <div>
          <ScreenOverlay apply={applyLoading} />
          <CustomGenericModal
          type={genericModalProps?.type}
          message={genericModalProps?.message}
          modalOpen={genericModalProps?.open}
          onClose={() => setGenericModalProps(initialGenericModalProps)}
        />
          <FormHeader
            parentLabel={"Batch & Expiry Management"}
            label={"Create new Batch ID"}
            goBackToParent={() => setOpenCreateBatchIdForm(false)}
          />
          <form onSubmit={handleSubmit} className="CreateBatchIdForm">
            <div className="FormTextField">
              <div className="FormTextField_label">
                SKU Code
                <span style={{ color: "red" }}>*</span>
              </div>
              <Autocomplete
                options={Object.keys(skuProductDescriptionMap)}
                getOptionLabel={(option) => option}
                value={formData.skuCode}
                onChange={handleSkuCodeChange}
                sx={{ width: "-webkit-fill-available" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="skuCode"
                    variant="outlined"
                    size="small"
                    sx={customMuiInputStyle}
                    required
                  />
                )}
              />
            </div>
            <div className="FormTextField">
              <div className="FormTextField_label">
                SKU Name
                <span style={{ color: "red" }}>*</span>
              </div>
              <Autocomplete
                options={Object.keys(skuNameToCodeMap)}
                getOptionLabel={(option) => option}
                value={formData.skuName}
                onChange={handleSkuNameChange}
                sx={{ width: "-webkit-fill-available" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="skuName"
                    variant="outlined"
                    size="small"
                    sx={customMuiInputStyle}
                    required
                  />
                )}
              />
            </div>
            <FormTextField
              name={"invoiceNumber"}
              value={formData.invoiceNumber}
              label={"Invoice Number"}
              handleChange={handleChange}
            />
            <FormTextField
              name={"batchId"}
              value={formData.batchId}
              label={"Batch ID"}
              handleChange={handleChange}
              isMandatory={true}
              isDisabled={!formData.skuCode && !formData.skuName}
              error={batchIdError}
            />
            <div style={{ position: "relative" }}>
              <FormTextField
                name={""}
                value={""}
                label={"Quantity"}
                handleChange={handleChange}
                isMandatory={true}
                isMultiQtyType={true}
                csQty={formData.quantityCs}
                pcQty={formData.quantityPc}
                otherQty={formData.quantityOther}
                disclaimer={true}
                uom={skuUomMap[formData.skuCode]}
              />
              {unallocatedQtyDisclamer && (
                <div
                  style={{
                    color: "#cd8e15",
                    display: "flex",
                    alignItems: "center",
                    lineHeight: "15px",
                    fontSize: "12px",
                    marginLeft: "10rem",
                    position: "absolute",
                    top: "42px",
                  }}
                >
                  {
                    <div
                      style={{
                        width: "20px",
                        paddingRight: "6px",
                        paddingTop: "2px",
                        fontSize: "11px",
                      }}
                    >
                      <RenderSvg name="warningIcon" />
                    </div>
                  }
                  {unallocatedQtyDisclamer}
                </div>
              )}
            </div>
            <FormDateField
              value={formData.expiryDate}
              label={"Expiry Date"}
              handleDateChange={handleDateChange}
              expiryError={expiryError}
              isMandatory={true}
            />
            <Box mt={6} display="flex" justifyContent="right" width={"95%"} margin={"0"}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isApplyDisabled}
                sx={{
                  backgroundColor: (isApplyDisabled) ? "#E0E0E0" : "primary.main",
                  color: (isApplyDisabled) ? "#A0A0A0" : "#FFFFFF",
                }}
              >
                {applyLoading && <><CircularProgress style={{...customStyle, color: "#FFFFFF", height: 17, width: 17}} />&nbsp; &nbsp;</>}
                APPLY
                &nbsp;<span style={{fontSize:"9.5px", textTransform:"none"}}>{positiveCta}</span>
              </Button>
            </Box>
          </form>
        </div>
      )}
      <BatchIdFormSubmitModal />
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
    </>
  );
};

export default CreateBatchIdForm;
